import React, { useEffect, useState, useRef } from "react";

function classNames(...xs) { return xs.filter(Boolean).join(" "); }

const AddressSuggestInput = ({ label, value, onChange, onSelect, suggestions, loading, onQuery }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);
  return (
    <div className="space-y-1" ref={ref}>
      <label className="text-sm font-medium text-white/80">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); onQuery?.(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Start typing an address..."
        className="bw-input mt-1"
      />
      {open && (loading || (suggestions && suggestions.length)) ? (
        <div className="mt-1 max-h-56 overflow-auto rounded-md border border-white/10 bg-black shadow-lg">
          {loading && <div className="px-3 py-2 text-sm bw-muted">Searching...</div>}
          {suggestions?.map((s) => (
            <button
              key={s.id || s.name}
              type="button"
              onClick={() => { onSelect(s.name); setOpen(false); }}
              className="block w-full cursor-pointer truncate px-3 py-2 text-left text-sm text-white hover:bg-white/10"
              title={s.name}
            >
              {s.name}
            </button>
          ))}
          {!loading && (!suggestions || suggestions.length === 0) && (
            <div className="px-3 py-2 text-sm bw-muted">No suggestions</div>
          )}
        </div>
      ) : null}
    </div>
  );
};

const EditProfileModal = ({ open, onClose, onSubmit, submitting, profile, apiError, registered, onDeleteAccount }) => {
  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [homeAddress, setHomeAddress] = useState("");
  const [workAddress, setWorkAddress] = useState("");
  const [errors, setErrors] = useState({});

  // wizard state
  const [step, setStep] = useState(1); // 1: name, 2: phone, 3: addresses (+ privacy)
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);

  // geolocation for biasing suggestions
  const [geo, setGeo] = useState(null);

  // suggestions state
  const [homeSugs, setHomeSugs] = useState([]);
  const [workSugs, setWorkSugs] = useState([]);
  const [loadingHome, setLoadingHome] = useState(false);
  const [loadingWork, setLoadingWork] = useState(false);
  const homeTimer = useRef(null);
  const workTimer = useRef(null);

  useEffect(() => {
    if (open) {
      setName(profile?.name || "");
      setPhoneNumber(profile?.phoneNumber || "");
      setEmail(profile?.email || "");
      setHomeAddress(profile?.homeAddress || "");
      setWorkAddress(profile?.workAddress || "");
      setErrors({});
      setHomeSugs([]);
      setWorkSugs([]);
      setStep(1);
      setAcceptPrivacy(false);

      if (navigator?.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const { latitude, longitude } = pos.coords || {};
            if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
              setGeo({ lat: latitude, lon: longitude });
            }
          },
          () => {},
          { enableHighAccuracy: true, timeout: 6000, maximumAge: 120000 }
        );
      }
    }
  }, [open, profile]);

  const querySuggest = (q, setSugs, setLoading, timerRef) => {
    if (!q || q.length < 3) { setSugs([]); return; }
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(async () => {
      try {
        setLoading(true);
        const base = process.env.REACT_APP_MINIAPP_API_BASE_URL || "/telegramMiniApp";
        const params = new URLSearchParams({ q });
        if (geo?.lat && geo?.lon) {
          params.set("lat", String(geo.lat));
          params.set("lon", String(geo.lon));
        }
        const resp = await fetch(`${base}/suggest?${params.toString()}`);
        const data = await resp.json();
        if (data?.success) setSugs(data.data || []); else setSugs([]);
      } catch (e) {
        setSugs([]);
      } finally {
        setLoading(false);
      }
    }, 250);
  };

  const validateStep = (s) => {
    const errs = {};
    if (s === 1) {
      if (!name.trim()) errs.name = "Name is required";
    } else if (s === 2) {
      if (phoneNumber && !/^\+?[0-9\-()\s]{6,20}$/.test(phoneNumber)) errs.phoneNumber = "Invalid phone";
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errs.email = "Invalid email";
    } else if (s === 3) {
      if (!registered && !acceptPrivacy) errs.acceptPrivacy = "You must accept the privacy policy";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onNext = () => {
    if (!validateStep(step)) return;
    setStep((p) => Math.min(3, p + 1));
  };
  const onBack = () => setStep((p) => Math.max(1, p - 1));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step < 3) {
      onNext();
      return;
    }
    if (!validateStep(3)) return;
    await onSubmit({ name: name.trim(), phoneNumber: phoneNumber.trim(), email: email.trim(), homeAddress: homeAddress.trim(), workAddress: workAddress.trim(), acceptPrivacy });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="w-full max-w-lg rounded-xl border border-white/10 bg-neutral-950 p-5 shadow-2xl shadow-black/60">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-white">{registered ? 'Update Profile' : 'Register'}</h2>
            {registered && onDeleteAccount && (
              <button
                type="button"
                onClick={onDeleteAccount}
                className="p-1.5 text-white/70 hover:text-white"
                aria-label="Delete account"
                title="Delete account"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                  <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
            )}
          </div>
          <button onClick={onClose} className="bw-btn-ghost">✕</button>
        </div>

        {apiError ? (
          <div className="mb-3 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">{apiError}</div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-3">
          {step === 1 && (
            <div>
              <label className="text-sm font-medium text-white/80">Name</label>
              <input type="text" className="bw-input mt-1" value={name} onChange={(e) => setName(e.target.value)} />
              {errors.name && <p className="text-xs italic text-red-300">{errors.name}</p>}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-white/80">Phone</label>
                <input type="tel" className="bw-input mt-1" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} />
                {errors.phoneNumber && <p className="text-xs italic text-red-300">{errors.phoneNumber}</p>}
              </div>
              <div>
                <label className="text-sm font-medium text-white/80">Email (optional, for browser login)</label>
                <input type="email" className="bw-input mt-1" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
                {errors.email && <p className="text-xs italic text-red-300">{errors.email}</p>}
              </div>
            </div>
          )}

          {step === 3 && (
            <>
              <AddressSuggestInput
                label="Home Address (optional)"
                value={homeAddress}
                onChange={setHomeAddress}
                onSelect={setHomeAddress}
                suggestions={homeSugs}
                loading={loadingHome}
                onQuery={(q) => querySuggest(q, setHomeSugs, setLoadingHome, homeTimer)}
              />

              <AddressSuggestInput
                label="Work Address (optional)"
                value={workAddress}
                onChange={setWorkAddress}
                onSelect={setWorkAddress}
                suggestions={workSugs}
                loading={loadingWork}
                onQuery={(q) => querySuggest(q, setWorkSugs, setLoadingWork, workTimer)}
              />

              {!registered && (
                <label className="mt-2 flex items-center gap-2 text-sm text-white/80">
                  <input type="checkbox" checked={acceptPrivacy} onChange={(e) => setAcceptPrivacy(e.target.checked)} />
                  I agree to the Privacy Policy
                </label>
              )}
              {errors.acceptPrivacy && <p className="text-xs italic text-red-300">{errors.acceptPrivacy}</p>}
            </>
          )}

          <div className="mt-4 flex items-center justify-between gap-2">
            <div className="text-xs bw-muted">Step {step} of 3</div>
            <div className="flex items-center gap-2">
              {step > 1 && (
                <button type="button" onClick={onBack} className="bw-btn-ghost">Back</button>
              )}
              {step < 3 && (
                <button type="button" onClick={onNext} className="bw-btn">Next</button>
              )}
              {step === 3 && (
                <button type="submit" disabled={submitting} className={classNames("bw-btn", submitting && "opacity-50")}>
                  {registered ? (submitting ? "Saving..." : "Save") : (submitting ? "Registering..." : "Accept & Continue")}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfileModal;
